import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { ChemicalDetails } from './chemicalData';

interface ChemicalDetailModalProps {
  open: boolean;
  chemical: ChemicalDetails | null;
  onClose: () => void;
}

const InfoField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      sx={{
        fontWeight: 600,
        color: '#64748b',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: '#1e293b',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.5,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const PictogramField: React.FC<{ renderIcon: () => string }> = ({ renderIcon }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      sx={{
        fontWeight: 600,
        color: '#64748b',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        mb: 0.8,
      }}
    >
      Pictograms
    </Typography>
    <Box
      sx={{
        width: 70,
        height: 70,
        border: '3px solid #EE7D11',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#fff',
        fontSize: '40px',
      }}
    >
      {renderIcon()}
    </Box>
  </Box>
);

export const ChemicalDetailModal: React.FC<ChemicalDetailModalProps> = ({
  open,
  chemical,
  onClose,
}) => {
  if (!chemical) return null;

  // Helper function to render pictogram image
  const renderPictogram = () => {
    const pictogramMap: { [key: string]: string } = {
      'Corrosive': '⚠️',
      'Toxic': '☠️',
      'Hazardous': '⚠️',
      'Flammable': '🔥',
    };
    return pictogramMap[chemical.pictogram] || '⚠️';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          background: '#ffffff',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(2px)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(110deg, #f4f8fd 0%, #e8f0fa 100%)',
          py: 2.5,
          px: 3,
          borderBottom: '3px solid #ee7d11',
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: 'rgba(0, 56, 131, 0.08)',
              borderLeft: '3px solid #ee7d11',
            }}
          >
            <Inventory2OutlinedIcon sx={{ color: '#003883', fontSize: 26 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#003883',
              fontSize: '18px',
              letterSpacing: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {chemical.chemicalName}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#003883',
            mt: -0.5,
            '&:hover': {
              backgroundColor: 'rgba(0, 56, 131, 0.08)',
              color: '#003883',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ py: 2.5, px: 3, backgroundColor: '#ffffff' }}>
        <PictogramField renderIcon={renderPictogram} />
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid item xs={6}>
            <InfoField label="EC No." value={chemical.ecNo} />
          </Grid>
          <Grid item xs={6}>
            <InfoField label="CAS No." value={chemical.casNo} />
          </Grid>
        </Grid>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="General Information" value={chemical.generalInformation} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="Product Name/Product Synonyms" value={chemical.productName} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="Hazard Codes" value={chemical.hazardCodes} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="Hazard Statements" value={chemical.hazardStatements} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="Hazard identification" value={chemical.hazardIdentification} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="Signal Word" value={chemical.signalWord} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 1.5 }}>
          <InfoField label="Physical Hazards" value={chemical.physicalHazards} />
        </Box>
        <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />

        <Box sx={{ mb: 0 }}>
          <InfoField label="Precautionary statements" value={chemical.precautionaryStatements} />
        </Box>
        {(chemical.unNumber || chemical.transportName || chemical.transportHazardClass || chemical.packingGroup) && (
          <>
            <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />
            <Grid container spacing={2}>
              {chemical.unNumber && (
                <Grid item xs={6}>
                  <InfoField label="UN Number" value={chemical.unNumber} />
                </Grid>
              )}
              {chemical.transportName && (
                <Grid item xs={6}>
                  <InfoField label="Transport Name" value={chemical.transportName} />
                </Grid>
              )}
              {chemical.transportHazardClass && (
                <Grid item xs={6}>
                  <InfoField label="Transport Hazard Class" value={chemical.transportHazardClass} />
                </Grid>
              )}
              {chemical.packingGroup && (
                <Grid item xs={6}>
                  <InfoField label="Packing Group" value={chemical.packingGroup} />
                </Grid>
              )}
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
